import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type ColumnDef,
  type Row,
  type RowData,
  type RowSelectionState,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';
import {
  row_getCanSelectSubRows,
  row_getIsSelected,
  table_getFilteredSelectedRowModel,
  table_setRowSelection,
} from '@tanstack/react-table/static-functions';

import type { RowSelectionFeatureConstructors } from './row-selection-types.js';
import { DATA_TABLE_ROW_SELECTION_COLUMN_ID } from '../../constants.js';

/**
 * Helper function to identify the ROW_SELECTION_COLUMN
 * @internal
 */
export function isRowSelectionColumnDef<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData,
>(column: ColumnDef<TFeatures, TData, TValue>): boolean {
  return column.id === DATA_TABLE_ROW_SELECTION_COLUMN_ID;
}

function mutateRowIsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  selectedRowIds: RowSelectionState,
  id: string,
  value: boolean,
  includeChildren: boolean,
  table: Table_Internal<TFeatures, TData>,
): void {
  const row = table.getRow(id, true);

  if (!row_getShouldDisableRowSelection(row)) {
    if (value) {
      if (
        row_getCanSelect(row) &&
        !table_getIsRowSelectionLimitReached(table, selectedRowIds)
      ) {
        selectedRowIds[id] = true;
      }
    } else {
      delete selectedRowIds[id];
    }
  }

  if (
    includeChildren &&
    row.subRows?.length &&
    row_getCanSelectSubRows(row) &&
    !table_getIsRowSelectionLimitReached(table, selectedRowIds)
  ) {
    row.subRows.forEach((row) =>
      mutateRowIsSelected(
        selectedRowIds,
        row.id,
        value,
        includeChildren,
        table,
      ),
    );
  }
}

export function table_resetBatchSelection<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): (event: MouseEvent) => void {
  table._batchSelectionRowId ??= undefined;
  table._previousSelectionRange ??= undefined;

  return (event: MouseEvent) => {
    if (!(table._batchSelectionRowId || table._previousSelectionRange)) {
      return;
    }

    const checkboxElement = (event.target as HTMLElement).closest(
      '[data-row-selection-checkbox]',
    );

    // Reset batch selection if click was outside of any checkbox
    if (checkboxElement === null) {
      table._batchSelectionRowId = undefined;
      table._previousSelectionRange = undefined;
      return;
    }

    // If click was on a checkbox, reset if the checkbox belongs to a different table
    if (
      table.getRowVirtualizationContainer()?.contains(checkboxElement) === false
    ) {
      table._batchSelectionRowId = undefined;
      table._previousSelectionRange = undefined;
    }
  };
}

/**
 * Helper function to get the row selection toggle handler for the "select all" checkbox in the header, which toggles the selection of all rows on the current page, taking into account any selection limits defined in the table options.
 * @internal
 */
export function table_getToggleAllLimitedRowsSelectedHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): (event: MouseEvent) => void {
  return (event: MouseEvent) => {
    table_toggleAllLimitedRowsSelected(
      table,
      (event.target as HTMLInputElement).checked,
    );
  };
}

/**
 * Helper function to get the row selection toggle handler for the "select all" checkbox in the header, which toggles the selection of all rows on the current page, taking into account any selection limits defined in the table options.
 * @internal
 */
export function table_getToggleAllLimitedPageRowsSelectedHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): (event: MouseEvent) => void {
  return (event: MouseEvent) => {
    table_toggleAllLimitedPageRowsSelected(
      table,
      (event.target as HTMLInputElement).checked,
    );
  };
}

/**
 * Helper function to get the count of currently selected rows in the table.
 * @internal
 */
export function table_getSelectionCount<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): number {
  return table_getFilteredSelectedRowModel(table).flatRows.length;
}

// You can access `rows`, `flatRows` and `rowsById` on the row model.
// Somehow the expanded detail rows are contained in the row model's `rowsById` which should actually not happen
// since we only adapt `rows` in our `getRowSelectionRowModel`.
// This leads to an issue when toggling the selection of all rows on the page:
// TanStack's implementation of `toggleAllPageRowsSelected` iterates over `rows` and would therefore try to select
// the expanded detail row which is not possible and causes the table to crash.
// Therefore, we need to overwrite this method and iterate over the `flatRows` to make sure only selectable rows
// are (de)selected.

/**
 * Helper function to toggle the selection of all rows on the current page, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are affected.
 * @internal
 */
export function table_toggleAllPageRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, value?: boolean): void {
  table_setRowSelection<TFeatures, TData>(table, (old) => {
    const resolvedValue = value ?? !table_getIsAllPageRowsSelected(table);

    const rowSelection: RowSelectionState = { ...old };

    table.getRowModel().flatRows.forEach((row) => {
      mutateRowIsSelected(
        rowSelection,
        row.id,
        resolvedValue,
        false, // we don't need to include children as they are already included in `flatRows`
        table,
      );
    });

    return rowSelection;
  });
}

/**
 * Override TanStack's implementation to avoid de-selecting disabled rows
 * @internal
 */
export function table_toggleAllRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, value?: boolean): void {
  table_setRowSelection(table, (old) => {
    const resolvedValue = value ?? !table_getIsAllRowsSelected(table);

    const rowSelection: RowSelectionState = { ...old };

    table.getPrePaginatedRowModel().flatRows.forEach((row) => {
      mutateRowIsSelected(
        rowSelection,
        row.id,
        resolvedValue,
        false, // we don't need to include children as they are already included in `flatRows`
        table,
      );
    });

    return rowSelection;
  });
}

export function table_toggleAllLimitedPageRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, value?: boolean): void {
  // Figure out what the target should be, as the "select all" can also be
  // indeterminate. Next steps are
  // false -> true
  // 'indeterminate' -> true
  // true -> false
  const resolvedValue = value ?? !table_getIsAllPageRowsSelected(table);

  // If we are triggering towards deselect, we can still use the limitless
  // selection
  if (resolvedValue === false) {
    table_toggleAllPageRowsSelected(table, false);
    return;
  }

  table_setRowSelection(table, (old) => {
    const rowSelection: RowSelectionState = { ...old };
    const limit = table.options.rowSelectionLimit ?? 0;

    // Figure out how many items are already selected.
    const currentSelectionLength = table_getSelectionCount(table);

    // If there are fewer elements selected than the limit we can fill the
    // selection.
    if (currentSelectionLength < limit) {
      const rows = table.getRowModel().rows;
      let currentSelectionCount = currentSelectionLength;
      for (const row of rows) {
        // If the row cannot be selected, or the element is already selected, we can skip over it
        if (!row_getCanSelect(row) || row_getIsSelected(row)) {
          continue;
        }
        rowSelection[row.id] = true;
        currentSelectionCount += 1;

        if (currentSelectionCount >= limit) {
          break;
        }
      }
    }
    return rowSelection;
  });
}

/**
 * Helper function to toggle the selection of all rows in the table, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are affected.
 * @internal
 */
export function table_toggleAllLimitedRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, value?: boolean): void {
  // Figure out what the target should be, as the "select all" can also be
  // indeterminate. Next steps are
  // false -> true
  // 'indeterminate' -> true
  // true -> false
  const resolvedValue = value ?? !table_getIsAllPageRowsSelected(table);

  // If we are triggering towards deselect, we can still use the limitless
  // selection
  if (resolvedValue === false) {
    table_toggleAllRowsSelected(table, false);
    return;
  }

  table_setRowSelection(table, (old) => {
    const rowSelection: RowSelectionState = { ...old };
    const limit = table.options.rowSelectionLimit ?? 0;

    // Figure out how many items are already selected.
    const currentSelectionLength = table_getSelectionCount(table);

    // If there are fewer elements selected than the limit we can fill the
    // selection.
    if (currentSelectionLength < limit) {
      const rows = table.getPrePaginatedRowModel().flatRows;
      let currentSelectionCount = currentSelectionLength;
      for (const row of rows) {
        // If the row cannot be selected, or the element is already selected, we can skip over it
        if (!row_getCanSelect(row) || row_getIsSelected(row)) {
          continue;
        }
        rowSelection[row.id] = true;
        currentSelectionCount += 1;

        if (currentSelectionCount >= limit) {
          break;
        }
      }
    }
    return rowSelection;
  });
}

/**
 * Helper function to determine if the row selection limit defined in the table options has been reached, based on the current row selection state.
 * @internal
 */
export function table_getIsRowSelectionLimitReached<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  rowSelectionState?: RowSelectionState,
): boolean {
  const rowSelection = rowSelectionState || table.atoms.rowSelection?.get();
  const rowSelectionLimit = table.options.rowSelectionLimit;
  const selectionCount = Object.keys(rowSelection ?? {}).length;

  return (
    rowSelectionLimit !== undefined &&
    rowSelectionLimit !== 0 &&
    selectionCount >= rowSelectionLimit
  );
}

/**
 * Helper function to determine if all rows on the current page are selected, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are considered.
 * @internal
 */
export function table_getIsAllPageRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  // if there is a limit for how many rows can be selected, and it is reached, immediately return true
  if (table_getIsRowSelectionLimitReached(table)) {
    return true;
  }

  return (
    table
      .getPaginatedRowModel()
      .flatRows.filter((row) => row.subRows.length === 0)
      // the header check box should be checked, when all rows are either selected or disabled
      .every((row) => row_getIsSelected(row) || !row_getCanSelect(row))
  );
}

/**
 * Helper function to determine if all rows in the table are selected, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are considered.
 * @internal
 */
export function table_getIsAllRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  // if there is a limit for how many rows can be selected, and it is reached, immediately return true
  if (table_getIsRowSelectionLimitReached(table)) {
    return true;
  }

  return (
    table
      .getPrePaginatedRowModel()
      .flatRows.filter((row) => row.subRows.length === 0)
      // the header check box should be checked, when all page rows are either selected or disabled
      .every((row) => row_getIsSelected(row) || !row_getCanSelect(row))
  );
}

/**
 * Helper function to determine if some (but not all) rows on the current page are selected, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are considered.
 * @internal
 */
export function table_getIsSomePageRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  return table
    .getPaginatedRowModel()
    .flatRows.filter((row) => row.subRows.length === 0)
    .some((row) => row_getIsSelected(row));
}

/**
 * Helper function to determine if some (but not all) rows in the table are selected, taking into account any selection limits defined in the table options, and ensuring that only selectable rows are considered.
 * @internal
 */
export function table_getIsSomeRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  return table
    .getPrePaginatedRowModel()
    .flatRows.filter((row) => row.subRows.length === 0)
    .some((row) => row_getIsSelected(row));
}

/**
 * Helper function to determine if the selection of all rows on the current page is disabled, based on the row selection state and configuration of each row, as well as any selection limits defined in the table options.
 * @internal
 */
export function table_getIsAllPageRowsSelectionDisabled<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  return table
    .getPaginatedRowModel()
    .flatRows.every((row) => row.getShouldDisableRowSelection());
}

/**
 * Helper function to determine if the selection of all rows in the table is disabled, based on the row selection state and configuration of each row, as well as any selection limits defined in the table options.
 * @internal
 */
export function table_getIsAllRowsSelectionDisabled<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  return table
    .getPrePaginatedRowModel()
    .flatRows.every((row) => row.getShouldDisableRowSelection());
}

/**
 * Helper function to determine if the selection of a specific row should be disabled, based on the row selection state and configuration of the row, as well as any selection limits defined in the table options.
 * @internal
 */
export function row_getShouldDisableRowSelection<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  const { enableRowSelection } = row.table.options;
  const isRowSelectionEnabled =
    typeof enableRowSelection === 'function'
      ? enableRowSelection(row)
      : enableRowSelection ?? true;

  // Selected rows should always be deselectable when limits are defined.
  // Otherwise, you could end up in a situation that you have reached a limit
  // but all options that would be deselectable to go under the limit are disabled.
  const hasSelectionLimit = row.table.options.rowSelectionLimit !== undefined;
  if (hasSelectionLimit && row_getIsSelected(row)) {
    return false;
  }

  return (
    !isRowSelectionEnabled || table_getIsRowSelectionLimitReached(row.table)
  );
}

/**
 * Helper function to determine if a specific row can be selected, based on the configuration of the row and any selection limits defined in the table options.
 * @internal
 */
export function row_getCanSelect<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  const isParentRow = !!row.subRows?.length;

  if (isParentRow) {
    return false;
  }

  const { enableRowSelection } = row.table.options;
  return typeof enableRowSelection === 'function'
    ? enableRowSelection(row)
    : enableRowSelection ?? true;
}

/**
 * Helper function to toggle the selection of a specific row, taking into account the configuration of the row and any selection limits defined in the table options. If the row is not selectable or selection is disabled, the function will have no effect.
 * @internal
 */
export function row_toggleSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, value?: boolean): void {
  if (row_getShouldDisableRowSelection(row)) {
    return;
  }
  const isSelected = row_getIsSelected(row);

  table_setRowSelection(row.table, (old) => {
    value = value ?? !isSelected;
    const selectedRowIds = { ...old };

    if (row_getCanSelect(row) && isSelected === value) {
      return old;
    }

    mutateRowIsSelected(selectedRowIds, row.id, value, true, row.table);

    return selectedRowIds;
  });
}

/**
 * Override TanStack's implementation to include selected, but disabled rows
 * Helper function to determine if some (but not all) sub-rows of a specific row are selected, based on the selection state of the sub-rows, the configuration of the sub-rows, and any selection limits defined in the table options.
 * @internal
 */
export function row_getIsSomeSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  const subRows = row.subRows;
  if (!subRows.length) {
    return row_getIsSelected(row);
  }
  return subRows.some(
    (subRow) => row_getIsSelected(subRow) || row_getIsSomeSelected(subRow),
  );
}

/**
 * Override TanStack's implementation to include selected, but disabled rows
 * Helper function to determine if all sub-rows of a specific row are selected, based on the selection state of the sub-rows, the configuration of the sub-rows, and any selection limits defined in the table options.
 * @internal
 */
export function row_getIsAllSubRowsSelected<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  const subRows = row.subRows;
  if (!subRows.length) {
    return row_getIsSelected(row);
  }
  for (const subRow of subRows) {
    if (!row_getIsAllSubRowsSelected(subRow)) {
      return false;
    }
  }
  return true;
}

/**
 * Handles row selection/deselection with support for batch selection using Shift+click. If Shift is not pressed,
 * or no previous selection exists, the row is simply selected/deselected and becomes the reference for a possible batch selection.
 * If a batch selection reference exists, and the user selects a row while holding Shift, all rows in between are selected/deselected.
 * If the user then selects another row with the Shift pressed, the previous batch selection is removed, and only the new one is selected.
 * The batch selection reference is reset, once the user clicks anywhere in the table outside the checkboxes.
 * @internal
 */
export function row_batchSelect<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, value?: boolean, shiftKey?: boolean) {
  const selectSingleRow = () => {
    row_toggleSelected(row, value);
    row.table._batchSelectionRowId = row.id;
    row.table._previousSelectionRange = undefined;
  };

  // if last selected row is undefined, or if the shift key is not pressed, simply toggle the single row selection
  if (row.table._batchSelectionRowId === undefined || !shiftKey) {
    selectSingleRow();
    return;
  }

  // if the shift key is pressed and there is a previous selection
  // get the start and end index of the batch selection
  const flatRows = row.table.getRowOrderRowModel().flatRows;
  const firstSelectedIndex = flatRows.findIndex(
    (flatRow) =>
      row.table._batchSelectionRowId &&
      flatRow.id === row.table._batchSelectionRowId,
  );
  const currentSelectedIndex = flatRows.findIndex(
    (flatRow) => flatRow.id === row.id,
  );

  // If the start row cannot be found (e.g. due to changing data) fall back to single selection
  if (firstSelectedIndex === -1) {
    selectSingleRow();
    return;
  }

  const startIndex = Math.min(firstSelectedIndex, currentSelectedIndex);
  const endIndex = Math.max(firstSelectedIndex, currentSelectedIndex);

  const shouldUpdateChildren = (idx: number) => endIndex === idx;

  table_setRowSelection(row.table, (old) => {
    const rowSelection: RowSelectionState = { ...old };
    // the batch selection checked value will be the same as for the initial selection
    const firstSelectedCheckedValue = row_getIsSelected(
      flatRows[firstSelectedIndex],
    );

    if (row.table._previousSelectionRange) {
      const start = row.table._previousSelectionRange.start;
      // Clamp end in case the data has shrunk since the last
      const end = Math.min(
        row.table._previousSelectionRange.end,
        flatRows.length - 1,
      );
      // deselect the previous range
      for (let i = start; i <= end; i++) {
        mutateRowIsSelected(
          rowSelection,
          flatRows[i].id,
          false,
          true,
          row.table,
        );
      }
    }

    // select/deselect current range
    for (let i = startIndex; i <= endIndex; i++) {
      mutateRowIsSelected(
        rowSelection,
        flatRows[i].id,
        firstSelectedCheckedValue,
        shouldUpdateChildren(i),
        row.table,
      );
    }

    // if there was a selection, save the range
    row.table._previousSelectionRange = firstSelectedCheckedValue
      ? { start: startIndex, end: endIndex }
      : undefined;

    return rowSelection;
  });
}

/**
 * Constructs the column row selection for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowSelectionFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<RowSelectionFeatureConstructors<TFeatures, TData>> {
  return {
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('rowSelectionFeature', prototype, table, {
        row_getShouldDisableRowSelection: {
          fn: (row) => row_getShouldDisableRowSelection(row),
        },
        row_getCanSelect: {
          fn: (row) => row_getCanSelect(row),
        },
        row_toggleSelected: {
          fn: (row, value?: boolean) => row_toggleSelected(row, value),
        },
        row_getIsSomeSelected: {
          fn: (row) => row_getIsSomeSelected(row),
        },
        row_getIsAllSubRowsSelected: {
          fn: (row) => row_getIsAllSubRowsSelected(row),
        },
        row_batchSelect: {
          fn: (row, value?: boolean, shiftKey?: boolean) =>
            row_batchSelect(row, value, shiftKey),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('rowSelectionFeature', table, {
        table_resetBatchSelection: {
          fn: () => table_resetBatchSelection(table),
        },
        table_getToggleAllLimitedRowsSelectedHandler: {
          fn: () => table_getToggleAllLimitedRowsSelectedHandler(table),
        },
        table_getToggleAllLimitedPageRowsSelectedHandler: {
          fn: () => table_getToggleAllLimitedPageRowsSelectedHandler(table),
        },
        table_getSelectionCount: { fn: () => table_getSelectionCount(table) },
        table_toggleAllPageRowsSelected: {
          fn: (value?: boolean) =>
            table_toggleAllPageRowsSelected(table, value),
        },
        table_toggleAllRowsSelected: {
          fn: (value?: boolean) => table_toggleAllRowsSelected(table, value),
        },
        table_toggleAllLimitedPageRowsSelected: {
          fn: (value?: boolean) =>
            table_toggleAllLimitedPageRowsSelected(table, value),
        },
        table_toggleAllLimitedRowsSelected: {
          fn: (value?: boolean) =>
            table_toggleAllLimitedRowsSelected(table, value),
        },
        table_getIsRowSelectionLimitReached: {
          fn: () => table_getIsRowSelectionLimitReached(table),
        },
        table_getIsAllPageRowsSelected: {
          fn: () => table_getIsAllPageRowsSelected(table),
        },
        table_getIsAllRowsSelected: {
          fn: () => table_getIsAllRowsSelected(table),
        },
        table_getIsSomePageRowsSelected: {
          fn: () => table_getIsSomePageRowsSelected(table),
        },
        table_getIsSomeRowsSelected: {
          fn: () => table_getIsSomeRowsSelected(table),
        },
        table_getIsAllPageRowsSelectionDisabled: {
          fn: () => table_getIsAllPageRowsSelectionDisabled(table),
        },
        table_getIsAllRowsSelectionDisabled: {
          fn: () => table_getIsAllRowsSelectionDisabled(table),
        },
      });
    },
  };
}

/**
 * Feature implementation for Row Selection.
 * @internal
 */
export const DataTableRowSelectionFeature = constructRowSelectionFeature();
